<template>
  <div class="reply-item">
    <div class="reply-main">
      <div class="reply-row-top">
        <span class="reply-user">
          {{ reply.trueName || reply.userName || '匿名' }}
        </span>
        <span v-if="reply.parentId" class="reply-target">
          回复 {{ reply.parentTrueName || '' }}
        </span>
        <span class="reply-text">{{ reply.content }}</span>
      </div>
      <div class="reply-row-bottom">
        <span class="reply-time">{{ displayTime }}</span>
        <span
          class="like-btn"
          :class="{ liked: localLiked }"
          @click="doLike"
        >
          <img :src="localLiked ? require('@/assets/img/liked.png') : require('@/assets/img/like.png')" class="action-icon-thumb" alt="赞" />
          <span v-if="localLikeCount > 0" class="like-count">{{ localLikeCount }}</span>
        </span>
        <span
          class="dislike-btn"
          :class="{ disliked: localDisliked }"
          @click="doDislike"
        >
          <img :src="localDisliked ? require('@/assets/img/liked.png') : require('@/assets/img/like.png')" class="action-icon-thumb flip" alt="踩" />
          <span v-if="localDislikeCount > 0" class="dislike-count">{{ localDislikeCount }}</span>
        </span>
        <el-button type="primary" link size="small" @click="toggleReplyInput">
          回复
        </el-button>
        <el-button
          v-if="canDelete"
          type="danger"
          link
          size="small"
          @click="handleDelete"
        >删除</el-button>
      </div>
    </div>

    <div v-if="showInput" class="reply-inline-input">
      <div class="mention-textarea-wrapper">
        <mention-popup
          :visible="replyMention.state.mentionVisible"
          :users="replyMention.state.filteredUsers"
          :active-index="replyMention.state.mentionActiveIdx"
          :keyword="replyMention.state.mentionKeyword"
          :loading="replyMention.state.loading"
          @select="onInlineMentionSelect"
          @update:activeIndex="replyMention.state.mentionActiveIdx = $event"
          @close="replyMention.closeMention()"
        />
        <department-popup
          :visible="replyDept.state.deptVisible"
          :departments="replyDept.state.filteredDepartments"
          :active-index="replyDept.state.deptActiveIdx"
          :keyword="replyDept.state.deptKeyword"
          @select="onInlineDeptSelect"
          @update:activeIndex="replyDept.state.deptActiveIdx = $event"
          @close="replyDept.closeDepartment()"
        />
        <el-input
          ref="inlineTextareaRef"
          v-model="inputText"
          type="textarea"
          :rows="2"
          :placeholder="'回复 ' + (reply.trueName || reply.userName || '匿名') + '：（输入@可提及用户，#可提及部门）'"
          maxlength="500"
          show-word-limit
          @input="onInlineInput"
          @keydown.native="onInlineKeydown"
          @blur="onInlineBlur"
        />
      </div>
      <div class="reply-inline-btns">
        <el-button size="small" @click="toggleReplyInput">取消</el-button>
        <el-button type="primary" size="small" :loading="submitting" @click="doReply">
          回复
        </el-button>
      </div>
    </div>

    <!-- 递归渲染子评论 -->
    <div v-if="reply.children && reply.children.length > 0" class="nested-replies">
      <reply-item
        v-for="child in reply.children"
        :key="child.id"
        :reply="child"
        @refreshed="$emit('refreshed')"
      />
    </div>
  </div>
</template>

<script>
import { toggleLike as toggleLikeApi, toggleDislike as toggleDislikeApi, deleteComment as deleteCommentApi, queryAllDepartment } from '@/api/screen'
import { useMention } from '@/composables/useMention'
import { useDepartmentMention } from '@/composables/useDepartmentMention'
import MentionPopup from './MentionPopup.vue'
import DepartmentPopup from './DepartmentPopup.vue'
import { filterSensitiveWords } from '@/utils/sensitiveWords'

export default {
  name: 'ReplyItem',
  components: {
    MentionPopup,
    DepartmentPopup
  },
  props: {
    reply: {
      type: Object,
      required: true
    }
  },
  inject: ['createComment', 'contentId', 'userStore'],
  data() {
    return {
      uid: sessionStorage.getItem('uid'),
      showInput: false,
      inputText: '',
      submitting: false,
      localLiked: this.reply.isLiked || false,
      localLikeCount: this.reply.likeCount || 0,
      localDisliked: this.reply.isDisliked || false,
      localDislikeCount: this.reply.dislikeCount || 0,
      inlineCursorAtDetect: 0,
      replyMention: null,
      replyDept: null
    }
  },
  created() {
    this.replyMention = useMention()
    this.replyDept = useDepartmentMention(queryAllDepartment)
  },
  computed: {
    canDelete() {
      return (
        this.uid == 10 ||
        this.uid == this.reply.userId
      )
    },
    displayTime() {
      const d = new Date(this.reply.createdAt)
      const now = new Date()
      const diff = now - d
      const eightHours = 8 * 3600000

      if (diff < 60000) return '刚刚'
      if (diff < eightHours) {
        if (diff < 3600000) return Math.floor(diff / 60000) + '分钟前'
        return Math.floor(diff / 3600000) + '小时前'
      }
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hours = String(d.getHours()).padStart(2, '0')
      const mins = String(d.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${mins}`
    }
  },
  watch: {
    'reply.isLiked': function(val) {
      this.localLiked = val || false
    },
    'reply.likeCount': function(val) {
      this.localLikeCount = val || 0
    },
    'reply.isDisliked': function(val) {
      this.localDisliked = val || false
    },
    'reply.dislikeCount': function(val) {
      this.localDislikeCount = val || 0
    }
  },
  methods: {
    getCursor() {
      if (!this.$refs.inlineTextareaRef) return 0
      const el = this.$refs.inlineTextareaRef.$el || this.$refs.inlineTextareaRef
      const textarea = el.querySelector ? el.querySelector('textarea') : el
      return textarea ? textarea.selectionEnd : 0
    },
    setCursor(pos) {
      this.$nextTick(() => {
        const el = this.$refs.inlineTextareaRef && (this.$refs.inlineTextareaRef.$el || this.$refs.inlineTextareaRef)
        const textarea = el && (el.querySelector ? el.querySelector('textarea') : el)
        if (textarea) {
          textarea.selectionStart = textarea.selectionEnd = pos
          textarea.focus()
        }
      })
    },
    onInlineInput() {
      this.inlineCursorAtDetect = this.getCursor()
      this.replyMention.detectMention(this.inputText, this.inlineCursorAtDetect)
      this.replyDept.detectDepartment(this.inputText, this.inlineCursorAtDetect)
    },
    onInlineKeydown(e) {
      if (this.replyDept.state.deptVisible) {
        if (e.key === 'ArrowDown') { e.preventDefault(); this.replyDept.moveDown() }
        else if (e.key === 'ArrowUp') { e.preventDefault(); this.replyDept.moveUp() }
        else if (e.key === 'Enter') { e.preventDefault(); this.applyInlineDept() }
        else if (e.key === 'Escape') { e.preventDefault(); this.replyDept.closeDepartment() }
        return
      }
      if (!this.replyMention.state.mentionVisible) return
      if (e.key === 'ArrowDown') { e.preventDefault(); this.replyMention.moveDown() }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.replyMention.moveUp() }
      else if (e.key === 'Enter') { e.preventDefault(); this.applyInlineMention() }
      else if (e.key === 'Escape') { e.preventDefault(); this.replyMention.closeMention() }
    },
    onInlineBlur() {
      this.replyMention.scheduleClose()
      this.replyDept.scheduleClose()
    },
    applyInlineMention(user) {
      const result = user
        ? this.replyMention.selectUser(user)
        : this.replyMention.confirmSelection()
      if (!result) return
      const before = this.inputText.slice(0, result.anchorPos)
      const after = this.inputText.slice(this.inlineCursorAtDetect)
      this.inputText = before + result.displayName + after
      this.setCursor(result.cursorAfter)
    },
    onInlineMentionSelect(user) {
      this.applyInlineMention(user)
    },
    applyInlineDept(dept) {
      const result = dept ? this.replyDept.selectDepartment(dept) : this.replyDept.confirmSelection()
      if (!result) return
      const before = this.inputText.slice(0, result.anchorPos)
      const after = this.inputText.slice(this.inlineCursorAtDetect)
      this.inputText = before + result.displayName + after
      this.setCursor(result.cursorAfter)
    },
    onInlineDeptSelect(dept) { this.applyInlineDept(dept) },
    doLike() {
      toggleLikeApi(this.reply.id).then(res => {
        if (res && res.code === 200) {
          this.$emit('refreshed')
        }
      })
    },
    doDislike() {
      toggleDislikeApi(this.reply.id).then(res => {
        if (res && res.code === 200) {
          this.$emit('refreshed')
        }
      })
    },
    toggleReplyInput() {
      this.showInput = !this.showInput
      if (this.showInput) {
        this.inputText = ''
      } else {
        this.replyMention.closeMention()
        this.replyDept.closeDepartment()
      }
    },
    doReply() {
      if (!this.inputText.trim()) return
      const { text, matchCount } = filterSensitiveWords(this.inputText.trim())
      if (matchCount > 0) {
        this.$message.warning(`内容包含 ${matchCount} 个敏感词，已自动替换为*`)
        this.inputText = text
      }
      this.submitting = true
      const affairId = typeof this.contentId === 'function' ? this.contentId() : this.contentId;
      this.createComment(affairId, {
        content: text,
        parentId: this.reply.id
      }).then(res => {
        if (res && res.code === 200) {
          this.$message.success('回复成功')
          this.inputText = ''
          this.showInput = false
          this.$emit('refreshed')
        }
      }).catch(() => {}).finally(() => {
        this.submitting = false
      })
    },
    handleDelete() {
      // 立即捕获当前评论id，避免异步回调中this.reply已变化
      const commentId = this.reply.id
      this.$confirm('确定要删除这条评论吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        deleteCommentApi(commentId).then(res => {
          if (res && res.code === 200) {
            this.$message.success('删除成功')
            this.$emit('refreshed')
          }
        }).catch(() => {
          this.$message.error('删除失败')
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.reply-item {
  padding: 2px 0;
}

.reply-main {
  padding: 5px 12px;
  font-size: 13px;
  line-height: 1.8;
  border-radius: 6px;
}

.reply-main:hover {
  background: #f0f2f5;
}

.reply-row-top {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
}

.reply-row-bottom {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
  color: #c0c4cc;
}

.reply-row-bottom ::v-deep .el-button {
  font-size: 12px;
  height: auto;
  padding: 2px 8px;
  color: #409EFF;
  border-radius: 4px;
  transition: all 0.2s ease;
  background: transparent;
}

.reply-row-bottom ::v-deep .el-button:hover {
  color: #66b1ff;
  background: rgba(64, 158, 255, 0.08);
}

.reply-row-bottom ::v-deep .el-button--danger {
  color: #f56c6c;
}

.reply-row-bottom ::v-deep .el-button--danger:hover {
  color: #f78989;
  background: rgba(245, 108, 108, 0.08);
}

.action-icon-thumb {
  width: 18px;
  height: 18px;
  display: inline-block;
  vertical-align: middle;
}

.action-icon-thumb.flip {
  transform: rotate(180deg);
}

.like-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 2px;
  color: #c0c4cc;
  transition: color 0.15s;
  user-select: none;
}

.like-btn:hover {
  color: #409EFF;
}

.like-btn.liked {
  color: #409EFF;
}

.dislike-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 2px;
  color: #c0c4cc;
  transition: color 0.15s;
  user-select: none;
}

.dislike-btn:hover {
  color: #E6A23C;
}

.dislike-btn.disliked {
  color: #E6A23C;
}

.like-count,
.dislike-count {
  font-size: 12px;
}

.reply-user {
  color: #409EFF;
  font-weight: 500;
}

.reply-target {
  color: #909399;
  font-size: 12px;
}

.reply-text {
  color: #303133;
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.reply-inline-input {
  margin: 4px 12px 8px;
  padding: 8px 10px;
  background: #fafbfc;
  border: 1px solid #e8eaed;
  border-radius: 8px;
}

.reply-inline-btns {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
}

.mention-textarea-wrapper {
  position: relative;
  width: 100%;
}

/* 嵌套回复样式 */
.nested-replies {
  margin-left: 16px;
  padding-left: 12px;
  border-left: 2px solid #e8eaed;
}
</style>
